/** external */
import _ from "lodash";

/** internal */
import { generateRandomId, getRoundedDecimalValue } from "Utils";
import {
  getFormattedQuestionScore,
  getQuestionTitle,
  isVisualWorksheetElement,
} from "Worksheet/modules/WorksheetUtils";
import {
  BLANKS_ANSWER_REGEX,
  FIELD_UID_SCORE,
  QUESTION_UID_MULTI_PART,
} from "Worksheet/modules/WorksheetConstants";

/**
 * @deprecated use getQuestionIndexV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
/**
 *
 * @param {*} sections
 * @param {*} questionResourceId
 * @returns Getting Question Index from list of question and sections
 */
export const getQuestionIndex = (sections, questionResourceId) => {
  let questionIndex = 1;
  for (const section of sections) {
    for (const question of _.get(section, "childrenV2.edges", [])) {
      const questionTypeUID = _.get(question, "node.item.questionType.uid", "");
      if (_.get(question, "node.id") === questionResourceId) {
        return questionIndex;
      }
      if (!isVisualWorksheetElement(questionTypeUID)) questionIndex++;
    }
  }
  return -1; // Return -1 if questionResourceId is not found
};

/**
 * @deprecated use getSectionDetailsV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
/**
 *
 * @param {*} section
 * @returns Extracting out the section score and section questions
 */
export const getSectionDetails = section => {
  const questions = _.get(section, "childrenV2.edges", []);
  const sectionScore = _.sumBy(questions, question => {
    const questionTypeUid = _.get(question, "node.item.questionType.uid", "");
    let intScore = 0;

    if (questionTypeUid === QUESTION_UID_MULTI_PART) {
      const subQuestions = _.get(question, "node.childrenV2.edges", []);
      _.forEach(subQuestions, subQuestion => {
        const subQuestionItem = _.get(subQuestion, "node.item");
        const scoreField = _.find(_.get(subQuestionItem, "fields", []), {
          uid: FIELD_UID_SCORE,
        });
        intScore += getFormattedQuestionScore(scoreField);
      });
    } else {
      const scoreField = _.find(_.get(question, "node.item.fields", []), {
        uid: FIELD_UID_SCORE,
      });
      intScore = getFormattedQuestionScore(scoreField);
    }

    if (_.isNumber(intScore)) return intScore;

    return _.toNumber(_.get(question, "node.item.questionScore", 0)) || 0;
  });

  const numberOfQuestions = _.size(
    _.filter(questions, question => {
      const questionTypeUid = _.get(question, "node.item.questionType.uid", "");
      return !isVisualWorksheetElement(questionTypeUid);
    })
  );

  return {
    sectionScore,
    numberOfQuestions,
  };
};

/**
 * @deprecated Use `getActiveQuestionV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
/**
 *
 * @param {*} sections
 * @param {*} activeQuestionId
 * @returns Question and Section Id from the list of sections
 */
export const getActiveQuestion = (sections, activeQuestionId) => {
  let question = {};
  let sectionId = null;
  let parentItemId = null;
  let prevQuestionResourceId = null;
  let prevQuestionId = null; //previous question's item id
  let questionIndex = 1;
  for (const section of sections) {
    if (sectionId) break;
    const questions = _.get(section, "childrenV2.edges", []);
    for (let i = 0; i < questions.length; i++) {
      const thisQuestion = questions[i];
      const thisQuestionId = _.get(thisQuestion, "node.item.id");
      if (thisQuestionId === activeQuestionId) {
        question = thisQuestion;
        sectionId = _.get(section, "id");
        parentItemId = _.get(section, "item.id");
        prevQuestionResourceId =
          i > 0 ? _.get(questions[i - 1], "node.id") : null;
        prevQuestionId = i > 0 ? _.get(questions[i - 1], "node.item.id") : null;
        break;
      }
      const questionTypeUID = _.get(thisQuestion, "node.item.questionType.uid");
      if (!isVisualWorksheetElement(questionTypeUID)) questionIndex++;
    }
  }

  // * Getting question data
  const questionData = _.get(question, "node.item", {});

  // * Getting AI conversation ID
  const questionAIConversationId = _.get(
    questionData,
    "aiConversation.edges.0.node.id",
    ""
  );

  return {
    question,
    questionData,
    sectionId, //section's resource id
    parentItemId, //section's item id
    questionIndex,
    questionAIConversationId,
    prevQuestionResourceId,
    prevQuestionId,
  };
};

/**
 * @deprecated Use `getQuestionInfoForAiAssistantV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
/**
 *
 * @param {*} activeQuestionId
 * @param {*} sections
 * @returns Question required information for ai assistant
 */
export const getQuestionInfoForAiAssistant = (
  t,
  activeQuestionId,
  sections
) => {
  if (!activeQuestionId) return {};

  const {
    question,
    sectionId,
    parentItemId, // section's item id
    questionData,
    questionIndex,
    questionAIConversationId,
    prevQuestionResourceId,
    prevQuestionId, // previous question's item id
  } = getActiveQuestion(sections, activeQuestionId);

  if (_.isEmpty(question)) return {};

  // * Getting question details
  const questionId = activeQuestionId;
  const questionResourceId = _.get(question, "node.id");
  const questionTypeUID = _.get(questionData, "questionType.uid");

  // * Getting formatted question title
  const { htmlString } = getQuestionTitle({ question: questionData, t });

  // * Handling issue for blanks question
  const regX = BLANKS_ANSWER_REGEX;
  const questionTitle = _.replace(htmlString, regX, "_______");

  return {
    questionId,
    questionIndex,
    questionTitle,
    sectionId,
    parentItemId,
    questionAIConversationId,
    questionResourceId,
    questionTypeUID,
    isBlankQuestion: _.isEmpty(questionTitle),
    putAfterItemId: prevQuestionResourceId,
    putAfterQuestionId: prevQuestionId,
  };
};

/**
 *
 * @param {*} learningGoalsDropdownOptions
 * @param {*} selectedQuestionFields
 * @returns Learning goal fields of question ai assistant
 */
export const getLearningGoalsFieldsData = (
  selectedQuestionFields,
  learningGoalsDropdownOptions
) => {
  // * Getting learning goals fields keys
  const learningGoalsFieldsKeys = _.map(learningGoalsDropdownOptions, "key");

  // * filtering learning goals fields and omiting properties
  const filteredlearningGoalsFields = _.filter(selectedQuestionFields, field =>
    _.includes(learningGoalsFieldsKeys, _.get(field, "uid"))
  );

  // * Mapping learning goals fields by omiting properties not needed
  const learningGoalsFields = _.map(filteredlearningGoalsFields, field => ({
    id: generateRandomId(),
    ..._.omit(field, ["__typename", "resolveFields", "id"]),
  }));

  return learningGoalsFields;
};

/**
 *
 * @param {*} section
 * @returns Extracting out the section score and section questions
 */
export const getSectionDetailsV2 = section => {
  const questions = _.get(section, "children", []);
  const sectionScore = _.sumBy(questions, question => {
    const questionTypeUid = _.get(question, "item.questionType.uid", "");
    let intScore = 0;

    if (questionTypeUid === QUESTION_UID_MULTI_PART) {
      const subQuestions = _.get(question, "children", []);
      _.forEach(subQuestions, subQuestion => {
        const subQuestionItem = _.get(subQuestion, "item");
        const scoreField = _.find(_.get(subQuestionItem, "fields", []), {
          uid: FIELD_UID_SCORE,
        });
        intScore += getFormattedQuestionScore(scoreField);
      });
    } else {
      const scoreField = _.find(_.get(question, "item.fields", []), {
        uid: FIELD_UID_SCORE,
      });
      intScore = getFormattedQuestionScore(scoreField);
    }

    if (_.isNumber(intScore)) return intScore;

    return _.toNumber(_.get(question, "item.questionScoreV2", 0)) || 0;
  });

  const numberOfQuestions = _.size(
    _.filter(questions, question => {
      const questionTypeUid = _.get(question, "item.questionType.uid", "");
      return !isVisualWorksheetElement(questionTypeUid);
    })
  );

  return {
    sectionScore: getRoundedDecimalValue(sectionScore),
    numberOfQuestions,
  };
};

/**
 * Gets the index of a question from sections using array functions
 * @param {Array} sections - The array of sections containing questions
 * @param {string} questionId - The ID of the question to find
 * @returns {number} The index of the question or -1 if not found
 */
export const getQuestionIndexV2 = (sections, questionId) => {
  let questionIndex = 1;

  const found = sections.some(section =>
    _.get(section, "children", []).some(question => {
      const questionTypeUID = _.get(question, "item.questionType.uid", "");

      if (_.get(question, "item.id") === questionId) {
        return true;
      }

      if (!isVisualWorksheetElement(questionTypeUID)) {
        questionIndex++;
      }

      return false;
    })
  );

  return found ? questionIndex : -1;
};

/**
 * Retrieves the active question object and related metadata from a list of sections.
 *
 * @param {Array} sections - Array of section objects, each containing questions as children.
 * @param {string|number} activeQuestionId - The ID of the active question to find.
 * @returns {Object} An object containing the active question related data.
 */
export const getActiveQuestionV2 = (sections, activeQuestionId) => {
  let question = {};
  let parentItemId = null;
  let prevQuestionId = null; //previous question's item id
  let questionIndex = 1;

  // * Getting active question from sections
  for (const section of sections) {
    if (parentItemId) break;
    const questions = _.get(section, "children", []);
    for (let i = 0; i < questions.length; i++) {
      const questionData = _.get(questions[i], "item", {});
      const questionId = _.get(questionData, "id");
      if (questionId === activeQuestionId) {
        question = questions[i];
        parentItemId = _.get(section, "item.id");
        prevQuestionId = i > 0 ? _.get(questions[i - 1], "item.id") : null;
        break;
      }
      const questionTypeUID = _.get(questionData, "questionType.uid");
      if (!isVisualWorksheetElement(questionTypeUID)) questionIndex++;
    }
  }

  // * Getting question data
  const questionData = _.get(question, "item", {});

  // * Getting AI conversation ID
  const questionAIConversationId = _.get(
    questionData,
    "aiConversation.edges.0.node.id",
    ""
  );

  return {
    question,
    questionData,
    parentItemId,
    questionIndex,
    questionAIConversationId,
    prevQuestionId,
  };
};

/**
 * Retrieves formatted information about the currently active question for use with the AI Assistant (V2).
 *
 * @param {Function} t - Translation function for localizing question titles.
 * @param {string} activeQuestionId - The ID of the currently active question.
 * @param {Array} sections - Array of section objects containing questions.
 * @returns {Object} An object containing details about the active question:
 */
export const getQuestionInfoForAiAssistantV2 = (
  t,
  activeQuestionId,
  sections
) => {
  if (!activeQuestionId) return {};

  const {
    parentItemId,
    questionData,
    questionIndex,
    questionAIConversationId,
    prevQuestionId,
  } = getActiveQuestionV2(sections, activeQuestionId);

  if (_.isEmpty(questionData)) return {};

  // * Getting question details
  const questionId = activeQuestionId;
  const questionTypeUID = _.get(questionData, "questionType.uid");

  // * Getting formatted question title
  const { htmlString } = getQuestionTitle({ question: questionData, t });

  // * Handling issue for blanks question
  const regX = BLANKS_ANSWER_REGEX;
  const questionTitle = _.replace(htmlString, regX, "_______");

  return {
    questionId,
    questionIndex,
    questionTitle,
    parentItemId,
    questionAIConversationId,
    questionTypeUID,
    isBlankQuestion: _.isEmpty(questionTitle),
    putAfterQuestionId: prevQuestionId,
  };
};
